import { Component, Injector, OnInit, ViewChild } from '@angular/core';
// eslint-disable-next-line no-restricted-imports
import { UntypedFormGroup } from '@angular/forms';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  apOptionalSignal$,
  BadgeType,
  FindControlBehaviorConfigDto,
  FindControlBehaviorDto,
  FindControlBehaviorSecurityDto,
} from 'webcommon/shared';
import { FindControlBehaviorConfigurationRepository } from 'webcommon/webapi';
import { ApFindControlModifyInformationTabComponent } from './ap-findcontrol-modify-information-tab.component';
import { ApFindControlModifySecurityTabComponent } from './ap-findcontrol-modify-security-tab.component';
import { ApAlertService, ApComplexModal, ApNotificationService } from 'webcommon/core-ui';

@Component({
  providers: [ ApAlertService, ],
  selector: 'ap-findcontrol-modify-modal',
  templateUrl: './ap-findcontrol-modify-modal.component.html',
  styleUrls: ['./ap-findcontrol-modify-modal.component.less'],
})
export class ApFindControlModifyModalComponent extends ApComplexModal<string> implements OnInit {
  public findControlBehaviorPk: string;
  public findControlBehaviorName: string;

  @ViewChild(ApFindControlModifyInformationTabComponent) informationTabControl: ApFindControlModifyInformationTabComponent;
  @ViewChild(ApFindControlModifySecurityTabComponent) securityTabControl: ApFindControlModifySecurityTabComponent;

  baseId = 'quick-search';
  selectedTabIndex = apOptionalSignal$(this, 0);
  badgeType = BadgeType.Urgent;
  informationTabErrorCount = 0;
  securityTabErrorCount = 0;

  findControlBehaviorConfig?: FindControlBehaviorConfigDto;
  findControlBehavior: FindControlBehaviorDto | null = null;
  findControlBehaviorSecurity: FindControlBehaviorSecurityDto | null = null;
  securityGroupUid = '';

  doesFindControlHaveSecurity = false;
  isLoading = true;

  loadingError = false;
  saving = false; // Indicates that the modal is saving

  parentFormGroup: UntypedFormGroup;

  constructor(
    elementInjector: Injector,
    private findControlBehaviorConfigRepository: FindControlBehaviorConfigurationRepository,
    private apNotificationService: ApNotificationService,
    private alertService: ApAlertService,
  ) {
    super(elementInjector);
  }

  override ngOnInit() {
    this.isLoading = true;

    super.ngOnInit();
    this.setupFormGroup();
    this.load();
  }

  private setupFormGroup(): void {
    this.parentFormGroup = new UntypedFormGroup({
      informationTab: new UntypedFormGroup({}),
      securityTab: new UntypedFormGroup({}),
    });
  }

  public load(): void {
    this.isLoading = true;

    this.findControlBehaviorConfigRepository.getFindControlBehaviorConfiguration(
      this.findControlBehaviorPk).pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this.onDestroy$),
      ).subscribe(
        (data) => this.onLoadFindControlSuccess(data),
        () => {
          this.loadingError = true;
          this.apNotificationService.displayStandardError();
        },
    );
  }

  onLoadFindControlSuccess(resultDto: FindControlBehaviorConfigDto): void {
    this.findControlBehaviorConfig = resultDto;
    this.findControlBehavior = resultDto.FindControlBehavior;
    this.findControlBehaviorSecurity = resultDto.FindControlBehaviorSecurity;

    if (this.findControlBehavior.SecurityGroupUid !== null
          && this.findControlBehavior.HasSecurity
          && this.findControlBehavior.AllowMaintenance) {
      this.securityGroupUid = this.findControlBehavior.SecurityGroupUid;
      this.doesFindControlHaveSecurity = true;
    }
  }

  public onErrorCountChangeInformationTab(errorCount: number): void {
    this.informationTabErrorCount = errorCount;
  }

  public onErrorCountChangeSecurityTab(errorCount: number): void {
    this.securityTabErrorCount = errorCount;
  }

  public onClose(): void {
    this.save();
  }

  public save(): void {
    if (this.saving) return;

    if (this.parentFormGroup.invalid) {
      this.selectTabWithValidationError(true);
      return;
    }

    this.saving = true;
    this.clearAllAlerts();

    const findControlBehaviorConfig: FindControlBehaviorConfigDto = {
      FindControlBehavior: this.informationTabControl.save(),
      FindControlBehaviorSecurity: {
        SecurityPermissions: this.findControlBehaviorSecurity?.SecurityPermissions,
        SecurityUserGroups: this.doesFindControlHaveSecurity
          ? this.securityTabControl.save()
          : null,
      },
    };

    this.findControlBehaviorConfigRepository.putFindControlBehaviorConfiguration(
      findControlBehaviorConfig).pipe(
        finalize(() => { this.saving = false; }),
        takeUntil(this.onDestroy$),
      ).subscribe(
        (saveResult) => {
          if (saveResult.IsSuccessful) {
            this.onSavingComplete();
          } else {
            this.alert('Invalid input - ' + saveResult.ValidationErrors[0], true);
          }
        },
        () => {
          this.apNotificationService.displayStandardError();
        },
      );
  }

  private selectTabWithValidationError(scrollToInvalidElement: boolean = false): void {
    if (this.parentFormGroup.controls.informationTab.invalid) {
        this.selectedTabIndex.set(0);
        if (scrollToInvalidElement) {
          setTimeout(() => { this.informationTabControl.scrollToValidationError(); });
        }
    } else if (this.parentFormGroup.controls.securityTab.invalid) {
        this.selectedTabIndex.set(1);
        if (scrollToInvalidElement) {
          setTimeout(() => { this.securityTabControl.scrollToValidationError(); });
        }
    }
  }

  public onSavingComplete(): void {
    this.saving = false;
    this.apClose(this.findControlBehaviorName);
  }

  public async onCancel(): Promise<void> {
    // if (this.parentFormGroup.dirty && !this.parentFormGroup.invalid && !this.saving) {
    //     const unsavedChangesText =
    //       'You have made changes to one or more tabs.' +
    //       ' Would you like to save your changes before closing?';
    //     const result = await this.apWarningUnsavedChangesModalService.openModal(unsavedChangesText)
    //       .catch(() => { this.apUserCancel(); });
    //     if (result) {
    //       this.save();
    //     } else {
    //       this.apUserCancel();
    //     }
    // } else {
    //     this.apUserCancel();
    // }
    this.apUserCancel();
  }

  private alert(text: string, clearAllAlerts: boolean) {
    if (clearAllAlerts){
      this.alertService.clearAllAlerts();
    }
    this.alertService.error(text);
  }

  private clearAllAlerts() {
    this.alertService.clearAllAlerts();
  }
}
